import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { scrollToId } from "../lib/scroll";

export const NAV_ITEMS = [
  { label: "Home", id: "home" },
  { label: "Servizi", id: "servizi" },
  { label: "Come lavoriamo", id: "come-lavoriamo" },
  { label: "Progetti", id: "progetti" },
  { label: "FAQ", id: "faq" },
  { label: "Contatti", id: "contatti" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const goTo = (id) => (e) => {
    e.preventDefault();
    setOpen(false);
    if (isHome) {
      scrollToId(`#${id}`);
    } else {
      navigate(`/#${id}`);
    }
  };

  return (
    <header className="sticky top-0 z-50 border-b border-line bg-white/90 backdrop-blur-md">
      <div className="mx-auto flex h-[4.5rem] max-w-6xl items-center justify-between px-5 sm:px-8">
        <a
          href="/#home"
          onClick={goTo("home")}
          aria-label="DS LAB STUDIO - Torna all'inizio"
          data-testid="header-logo"
          className="shrink-0"
        >
          <Logo variant="dark" />
        </a>

        <nav aria-label="Navigazione principale" className="hidden lg:block">
          <ul className="flex items-center gap-7">
            {NAV_ITEMS.map((item) => (
              <li key={item.id}>
                <a
                  href={`/#${item.id}`}
                  onClick={goTo(item.id)}
                  data-testid={`nav-${item.id}`}
                  className="text-sm font-medium text-steel transition-colors duration-200 hover:text-navy-dark"
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="/#contatti"
            onClick={goTo("contatti")}
            data-testid="header-cta-button"
            className="hidden rounded-lg bg-navy px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:bg-navy-dark hover:shadow-md active:scale-[0.98] sm:inline-block"
          >
            Parlaci del tuo progetto
          </a>
          <button
            type="button"
            data-testid="mobile-menu-button"
            aria-expanded={open}
            aria-controls="mobile-menu"
            aria-label={open ? "Chiudi il menu" : "Apri il menu"}
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-line text-navy-dark lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <nav id="mobile-menu" aria-label="Navigazione mobile" className="border-t border-line bg-white lg:hidden">
          <ul className="mx-auto max-w-6xl space-y-1 px-5 py-4">
            {NAV_ITEMS.map((item) => (
              <li key={item.id}>
                <a
                  href={`/#${item.id}`}
                  onClick={goTo(item.id)}
                  data-testid={`mobile-nav-${item.id}`}
                  className="block rounded-lg px-3 py-3 text-base font-medium text-navy-dark transition-colors duration-200 hover:bg-mist"
                >
                  {item.label}
                </a>
              </li>
            ))}
            <li className="pt-2">
              <a
                href="/#contatti"
                onClick={goTo("contatti")}
                data-testid="mobile-cta-button"
                className="block rounded-lg bg-navy px-4 py-3.5 text-center text-base font-semibold text-white"
              >
                Parlaci del tuo progetto
              </a>
            </li>
          </ul>
        </nav>
      )}
    </header>
  );
};

export const Footer = () => {
  const year = new Date().getFullYear();
  return (
    <footer className="bg-navy-dark text-white" data-testid="site-footer">
      <div className="mx-auto max-w-6xl px-5 py-14 sm:px-8">
        <div className="flex flex-col gap-10 md:flex-row md:items-start md:justify-between">
          <div className="max-w-xs">
            <Logo variant="light" />
            <p className="mt-5 text-sm leading-relaxed text-white/60">
              Siti web professionali progettati intorno alla tua attività.
            </p>
          </div>
          <nav aria-label="Collegamenti sito" className="md:pt-1">
            <ul className="grid grid-cols-2 gap-x-12 gap-y-3 sm:grid-cols-1">
              {NAV_ITEMS.slice(1).map((item) => (
                <li key={item.id}>
                  <a
                    href={`/#${item.id}`}
                    data-testid={`footer-link-${item.id}`}
                    className="text-sm text-white/70 transition-colors duration-200 hover:text-white"
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
          <nav aria-label="Documenti legali" className="md:pt-1">
            <ul className="space-y-3">
              <li>
                <Link to="/privacy-policy" data-testid="footer-link-privacy" className="text-sm text-white/70 transition-colors duration-200 hover:text-white">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/cookie-policy" data-testid="footer-link-cookie" className="text-sm text-white/70 transition-colors duration-200 hover:text-white">
                  Cookie Policy
                </Link>
              </li>
              <li>
                <Link to="/termini-e-condizioni" data-testid="footer-link-terms" className="text-sm text-white/70 transition-colors duration-200 hover:text-white">
                  Termini e condizioni
                </Link>
              </li>
            </ul>
          </nav>
        </div>
        <div className="mt-12 border-t border-white/10 pt-6">
          <p className="text-xs text-white/50">© {year} DS LAB STUDIO — Tutti i diritti riservati</p>
        </div>
      </div>
    </footer>
  );
};
